import React, { useCallback, useEffect, useState } from "react";
import BorderedButton from "../../../Buttons/BorderedButton";
import PrimaryButton from "../../../Buttons/PrimaryButton";
import CustomRadio from "../../../CustomRadio/CustomRadio";
import { getSaleRefundPreview } from "../../../../APICalling/APICalling";
import styles from "./Style.module.css";

const CommissionHandlingStep = ({ saleId, backStep, nextStep, onCommissionChange }) => {
	const [commissionItems, setCommissionItems] = useState([]);
	const [commissionChoices, setCommissionChoices] = useState({});
	const [remark, setRemark] = useState("");
	const [loading, setLoading] = useState(false);

	const formatAmount = (price, currency = "SGD") => {
		const amount = Number(price || 0).toFixed(2);
		return `${currency === "SGD" ? "$" : ""}${amount}`;
	};

	const formatServiceName = (item) => {
		const duration = item?.duration ? `${item.duration} Mins ` : "";
		return `${duration}${item?.service_name || ""}`.trim();
	};

	const getAddonNames = (item) => {
		const addons = item?.booking_details?.addons;
		if (!Array.isArray(addons)) return "";
		return addons
			.map((addon) => addon?.addon_name)
			.filter(Boolean)
			.join(", ");
	};

	const getPagerBadgeClass = (gender) => {
		const normalizedGender = String(gender || "").toLowerCase();
		if (normalizedGender === "male") return `${styles.pagerBadge} ${styles.malePagerBadge}`;
		return styles.pagerBadge;
	};

	const fetchRefundPreview = useCallback(async () => {
		try {
			setLoading(true);
			if (!saleId) {
				setCommissionItems([]);
				setCommissionChoices({});
				setRemark("");
				return;
			}

			const response = await getSaleRefundPreview(saleId);
			const previewData = response?.data?.data;
			const bookingItems = Array.isArray(previewData?.booking_items) ? previewData.booking_items : [];
			setCommissionItems(bookingItems);
			setCommissionChoices(
				bookingItems.reduce((choices, item) => ({
					...choices,
					[item.booking_item_id]: "clawback"
				}), {})
			);
		} catch (error) {
			console.error("Error fetching refund preview:", error);
			setCommissionItems([]);
			setCommissionChoices({});
		} finally {
			setLoading(false);
		}
	}, [saleId]);

	useEffect(() => {
		fetchRefundPreview();
	}, [fetchRefundPreview]);

	const handleChange = (itemId, value) => {
		setCommissionChoices((choices) => ({
			...choices,
			[itemId]: value
		}));
	};

	useEffect(() => {
		const bookingItemIds = commissionItems.map((item) => ({
			booking_item_id: item.booking_item_id,
			clawback: commissionChoices[item.booking_item_id] === "clawback"
		}));

		onCommissionChange?.({
			clawback_commission: bookingItemIds.some((item) => item.clawback),
			booking_item_ids: bookingItemIds,
			remark: remark.trim()
		});
	}, [commissionChoices, commissionItems, onCommissionChange, remark]);

	return (
		<div className={`${styles.commissionStep} popup`}>
			<div className="header">
				<h2>Commission Handling</h2>
			</div>

			<div className="body">
				{loading ? (
					<div className={styles.loading}>Loading...</div>
				) : commissionItems.length > 0 ? (
					<>
						{commissionItems.map((item) => (
							<div key={item.booking_item_id} className={styles.commissionItem}>
								<div className={styles.serviceCard}>
									<div>
										<h6>{formatServiceName(item)}</h6>
										<p>
											<span className={styles.withLabel}>With:</span>{" "}
											{item?.therapist_pager_number && (
												<span className={getPagerBadgeClass(item?.therapist_gender)}>{item.therapist_pager_number}</span>
											)}
											{item?.therapist_name && (
												<span className={styles.therapistName}> {item.therapist_name}*</span>
											)}
										</p>
										{getAddonNames(item) && (
											<p>
												<span className={styles.withLabel}>Addons:</span>{" "}
												<span className={styles.therapistName}>{getAddonNames(item)}</span>
											</p>
										)}
									</div>
									<strong>{formatAmount(item?.price, item?.booking_details?.currency)}</strong>
								</div>

								<CustomRadio
									label="Clawback commission"
									subText="Deducted from the payroll on the day of the refund. Sales remain unaffected."
									name={`commission-${item.booking_item_id}`}
									value="clawback"
									checked={commissionChoices[item.booking_item_id] === "clawback"}
									onChange={(event) => handleChange(item.booking_item_id, event.target.value)}
									customClass={styles.commissionRadio}
								/>
								<CustomRadio
									label="Do not clawback commission"
									subText="No changes is made to commission payout."
									name={`commission-${item.booking_item_id}`}
									value="noClawback"
									checked={commissionChoices[item.booking_item_id] === "noClawback"}
									onChange={(event) => handleChange(item.booking_item_id, event.target.value)}
									customClass={styles.commissionRadio}
								/>
							</div>
						))}
						{/* <div className={styles.remarkField}>
							<label htmlFor="commission-refund-remark">Remarks</label>
							<textarea
								id="commission-refund-remark"
								value={remark}
								onChange={(event) => setRemark(event.target.value)}
								placeholder="Reason for refund"
								rows={2}
							/>
						</div> */}
					</>
				) : (
					<div className={styles.loading}>No commission details found.</div>
				)}
			</div>

			<div className="footer">
				<BorderedButton text="Back" onClick={backStep} />
				<PrimaryButton text="Next" onClick={nextStep} disabled={loading} />
			</div>
		</div>
	);
};

export default CommissionHandlingStep;
